import { openDialog } from "@/src/components/dialogs";
import Loading from "@/src/components/loading";
import { Input } from "@/src/components/universalInput";
import {
  Field,
  FieldAnswer,
  FieldOption,
  FieldsSection,
  FolderItem,
  ManualVariable,
  manualVariables,
  Patient,
  RefAnswer,
  Reference,
} from "@/src/data/allTypes";
import {
  escapeId,
  getItemId,
  getUserId,
  jsonAbstract,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useState } from "react";
import { AiAudioFormButton } from "../documents/aiAudioFormButton";
import {
  shouldShowField,
  shouldShowSection,
} from "../documents/documentConditions";
import { getManualVariableValue } from "../documents/manualValuesVariables";
import {
  getSimpleAntecedentsText,
  getSimpleFamilialAntecedentsText,
} from "../documents/textGenerationUtils";
import { ExpansionTile } from "../landingPage/faqSection";
import { FieldAnswerAiEdit } from "../aiTasks/AiEditWrapper";

export function ReferenceFieldsForm({
  patient,
  item,
  reference,
  currentAnswers,
  isHorizontal = true,
  specificFieldIds,
  showSectionTitles = true,
  onChange,
}: {
  patient: Patient | null | undefined;
  item?: FolderItem | null | undefined;
  reference: Reference | null | undefined;
  currentAnswers: (FieldAnswer | null | undefined)[];
  isHorizontal?: boolean;
  specificFieldIds?: string[];
  showSectionTitles?: boolean;
  onChange: (newValue: (FieldAnswer | null | undefined)[]) => void;
}) {
  const [uniqueId, setUniqueId] = useState(uid());
  const [reload, setReload] = useState(0);
  const [edits, setEdits] = useState<Record<string, FieldAnswerAiEdit>>({});

  const [localFieldAnswers, setLocalFieldAnswers] = useState(currentAnswers);
  function getValue(value: any): any {
    if (Array.isArray(value))
      return value.map((v) => (v?.id?.includes("uploads:") ? v : getValue(v)));
    if (value?.id) return value.id;
    return value;
  }

  function getDisplayableValue(
    value: any,
    options: (FieldOption | null | undefined)[]
  ): any {
    if (Array.isArray(value))
      return value.map((v) => getDisplayableValue(v, options));
    let val = value?.id || value;
    let option = options?.find((option) => option?.id === val);

    let name = option?.name || val;
    if (name == "true") return "oui";
    if (name == "false") return "non";
    return name;
  }

  function removeInvisibleFields(
    newFieldAnswers: (FieldAnswer | null | undefined)[]
  ) {
    newFieldAnswers = newFieldAnswers.filter((fieldAnswer) => {
      let field = fieldAnswer?.field;
      let section = reference?.sections?.find((section) =>
        section?.fields?.find((f) => f?.id == field?.id)
      );
      if (!section) return false;
      if (
        !shouldShowSection({
          section,
          fieldAnswers: localFieldAnswers,
          patient,
          reference,
        })
      )
        return false;

      if (!field) return false;

      return shouldShowField({
        field,
        fieldAnswers: localFieldAnswers,
        patient,
        reference,
      });
    });
    onChange(newFieldAnswers);
  }

  async function getSpecialValue(
    fieldAnswer: FieldAnswer | null | undefined,
    field: Field
  ) {
    let fieldValue = getValue(fieldAnswer?.value) || field?.defaultValue;

    if (
      !fieldAnswer?.value &&
      field?.name?.toLowerCase() == "nationalité" &&
      patient?.nationality
    ) {
      let option = field?.options?.find(
        (option) =>
          option?.name?.toLowerCase() == patient?.nationality?.toLowerCase()
      );

      return option?.id;
    }

    if (
      !fieldAnswer?.value &&
      field?.name?.toLowerCase() == "profession" &&
      patient?.profession
    ) {
      let option = field?.options?.find(
        (option) =>
          option?.name?.toLowerCase() == patient?.profession?.toLowerCase()
      );

      return option?.id;
    }

    if (
      !fieldAnswer?.value &&
      manualVariables[fieldValue as ManualVariable] != null &&
      item
    ) {
      let value = await getManualVariableValue({
        variableName: fieldValue as ManualVariable,
        item,
      });
      return value || "";
    }

    if (fieldValue == "PATIENT_ANTECEDENTS")
      return await getSimpleAntecedentsText({
        patient,
      });

    if (fieldValue == "PATIENT_FAMILIAL_ANTECEDENTS")
      return await getSimpleFamilialAntecedentsText({
        patient,
      });

    return fieldValue || null;
  }

  function getAdditionalClasses(
    displayableValue: string,
    defaultValue: string
  ) {
    const trimmedValue = displayableValue?.toLowerCase().trim();
    if (trimmedValue == "oui" || trimmedValue == "fait")
      if (trimmedValue == "oui" || trimmedValue == "fait")
        return "green-bg white";
    if (trimmedValue == "non" || trimmedValue == "non fait")
      return "red-bg white";
    if (trimmedValue) return "blue-bg white";
    if (defaultValue) return "gray-bg ";
    return "white-bg";
  }

  function isFirstField(field: Field, section: FieldsSection) {
    return (
      getItemId(section?.fields?.[0]) == getItemId(field) ||
      getItemId(specificFieldIds?.[0]) == getItemId(field)
    );
  }

  return (
    <div className="c1 gap30">
      <div
        style={{ position: "absolute", top: 10, right: 10, zIndex: 9999999 }}
      >
        <AiAudioFormButton
          key={`${reload}${JSON.stringify(localFieldAnswers)}AiAudioFormButton`}
          patient={patient}
          reference={reference}
          currentFieldAnswers={localFieldAnswers?.filter(
            (fieldAnswer) => fieldAnswer != null
          )}
          onSuccess={(fieldAnswers, edits) => {
            let newFieldAnswers = [...localFieldAnswers];
            for (let fieldAnswer of fieldAnswers) {
              if (!fieldAnswer) continue;
              let existingFieldAnswer = newFieldAnswers.find(
                (existingFieldAnswer) =>
                  getItemId(existingFieldAnswer?.field) ==
                  getItemId(fieldAnswer?.field)
              );

              if (existingFieldAnswer) {
                newFieldAnswers = newFieldAnswers.filter(
                  (existingFieldAnswer) =>
                    getItemId(existingFieldAnswer?.field) !=
                    getItemId(fieldAnswer?.field)
                );
              }
              newFieldAnswers.push(fieldAnswer);
            }
            setEdits(edits);
            setLocalFieldAnswers(newFieldAnswers);
            setReload((reload) => reload + 1);
            onChange(newFieldAnswers);
          }}
        ></AiAudioFormButton>
      </div>
      {reference?.sections?.map((section) => {
        if (!section) return null;
        let fields =
          section?.fields?.filter(
            (field) =>
              field &&
              field?.name != "Nouvel élément" &&
              (specificFieldIds == null ||
                specificFieldIds.includes(getItemId(field)))
          ) || [];
        if (fields.length == 0) return null;

        if (
          !shouldShowSection({
            section,
            fieldAnswers: localFieldAnswers,
            patient,
            reference,
          })
        )
          return null;

        if (section.type == "table") {
          return (
            <div className="c1 gap10" key={`${section.id}Table`}>
              {showSectionTitles === false ? null : (
                <div className="bold f16">{section.name}</div>
              )}
              <table>
                <tr>
                  <th></th>
                  {section.tableColumns?.map((column) => {
                    if (!column) return null;
                    return (
                      <th key={uid()} className="white-bg">
                        {column}
                      </th>
                    );
                  })}
                </tr>
                {section.tableRows?.map((row) => {
                  return (
                    <tr key={uid()}>
                      <td className="white-bg">{row}</td>
                      {section.tableColumns?.map((column) => {
                        if (!column) return null;
                        const field = fields?.find(
                          (f) => f?.id == escapeId("fields", `${row}_${column}`)
                        );

                        if (!field)
                          return <td key={uid()} className="tcenter"></td>;

                        let fieldAnswer = localFieldAnswers.find(
                          (fieldAnswer) => fieldAnswer?.field?.id === field?.id
                        );

                        let fieldValue = getValue(fieldAnswer?.value);

                        const displayableValue = getDisplayableValue(
                          fieldValue,
                          field?.options || []
                        );

                        return (
                          <td
                            key={`${field.id}FieldContainer`}
                            className={`${getAdditionalClasses(
                              displayableValue,
                              field?.defaultValue
                            )}  fade`}
                            onClick={async () => {
                              let newInputValue =
                                fieldValue || field.defaultValue;
                              await openDialog({
                                id: "inputDialog",
                                title: field?.name,
                                content: (
                                  <div style={{ minWidth: 350, width: "70vw" }}>
                                    <Input
                                      key={`${field.id}Field`}
                                      shouldFocus={
                                        isFirstField(field, section) &&
                                        (reference?.sections?.length || 0) < 3
                                      }
                                      type={jsonAbstract(field?.type)}
                                      rows={6}
                                      isHorizontal={isHorizontal}
                                      name={""}
                                      unitName={field?.unit?.name}
                                      isRequired={field?.isRequired === true}
                                      value={newInputValue}
                                      options={field?.options
                                        ?.filter((a) => a)
                                        .map((option) => {
                                          return {
                                            value: option!.id,
                                            label: option!.name,
                                          };
                                        })}
                                      onChange={(newValue) => {
                                        fieldAnswer ||= {
                                          field,
                                          value: null,
                                          parent: null,
                                          patient: null,
                                          creator: getUserId(),
                                        };
                                        fieldAnswer.value = newValue;

                                        console.log(
                                          "the new dedefef value",
                                          newInputValue
                                        );

                                        const exists = localFieldAnswers.find(
                                          (fieldAnswer) =>
                                            getItemId(fieldAnswer?.field) ==
                                            getItemId(field)
                                        );

                                        if (!exists)
                                          localFieldAnswers.push(fieldAnswer);

                                        removeInvisibleFields([
                                          ...localFieldAnswers,
                                        ]);
                                        setLocalFieldAnswers([
                                          ...localFieldAnswers,
                                        ]);
                                      }}
                                    />
                                  </div>
                                ),
                                onConfirm: () => {
                                  onChange([...localFieldAnswers]);
                                },
                              });
                            }}
                          >
                            <div className="tcenter">
                              {displayableValue || "-"}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </table>
            </div>
          );
        }

        return (
          <ReferenceFieldsFormSection
            key={`${section.id}${reload}Section`}
            section={section}
            showSectionTitle={showSectionTitles}
          >
            {fields?.map((field) => {
              if (!field) return null;
              let fieldAnswer = localFieldAnswers.find(
                (fieldAnswer) =>
                  getItemId(fieldAnswer?.field) === getItemId(field)
              );

              if (
                !shouldShowField({
                  field,
                  fieldAnswers: localFieldAnswers,
                  patient,
                  reference,
                })
              )
                return null;
              // console.log("should");

              return (
                <div
                  key={`${field.id}${reload}FieldContainer`}
                  className="fade"
                >
                  <PromiseComponent
                    promise={getSpecialValue(fieldAnswer, field)}
                    queryKey={[
                      uniqueId,
                      reload,
                      `fieldValue-${field.id}`,
                      patient?.id || "123456789",
                      reference?.id || "123456789",
                      field.id,
                    ]}
                  >
                    {(fieldValue) => {
                      if (fieldValue) {
                        console.log("the fieldValiue", fieldValue);
                      }

                      return (
                        <Input
                          key={`${field.id}${reload}Field`}
                          type={jsonAbstract(field?.type)}
                          shouldFocus={
                            isFirstField(field, section) &&
                            (reference?.sections?.length || 0) < 3
                          }
                          aiEdit={edits[getItemId(field)]}
                          rows={6}
                          isHorizontal={isHorizontal}
                          additionalUploadData={
                            ["upload", "uploads"].includes(field.type || "none")
                              ? {
                                  patient: getItemId(patient),
                                  field: field?.id,
                                }
                              : {}
                          }
                          name={field?.name}
                          unitName={field?.unit?.name}
                          isRequired={field?.isRequired === true}
                          value={fieldValue}
                          options={field?.options
                            ?.filter((a) => a)
                            .map((option) => {
                              return { value: option!.id, label: option!.name };
                            })}
                          onChange={(newValue) => {
                            console.log("the new value", newValue);
                            fieldAnswer ||= {
                              field,
                              value: null,
                              parent: null,
                              patient: null,

                              creator: getUserId(),
                            };
                            fieldAnswer.value = newValue;

                            console.log("the new dedefef value", newValue);

                            let exists = localFieldAnswers.find(
                              (fieldAnswer) =>
                                fieldAnswer?.field?.id === field.id
                            );

                            if (!exists) localFieldAnswers.push(fieldAnswer);
                            removeInvisibleFields([...localFieldAnswers]);
                            setLocalFieldAnswers([...localFieldAnswers]);
                          }}
                        />
                      );
                    }}
                  </PromiseComponent>
                </div>
              );
            })}
          </ReferenceFieldsFormSection>
        );
      })}
    </div>
  );
}

export function ReferenceFieldsFormSection({
  section,
  children,
  showSectionTitle = true,
}: {
  section: FieldsSection;
  children: ReactNode;
  showSectionTitle?: boolean;
}) {
  if (section.isExpandable == true)
    return (
      <div className="c1 fade gap20">
        <ExpansionTile title={section.name} isInitalyExpanded={true}>
          {children}
        </ExpansionTile>
      </div>
    );

  return (
    <div className="c1 fade gap20">
      {showSectionTitle === false ? null : (
        <div className="bold f16">{section.name}</div>
      )}
      {children}
    </div>
  );
}

export function PromiseComponent({
  queryKey,
  promise,
  children,
}: {
  queryKey: any[];
  promise: Promise<any>;
  children: (data: any | null | undefined) => any;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => promise,
  });

  return (
    <div>
      {isLoading && <Loading></Loading>}
      {/* {isError && data && <div>Erreur: {error.message}</div>} */}
      <div key={`${JSON.stringify(queryKey)}${data}children`}>
        {children(data)}
      </div>
    </div>
  );
}

export function RefAnswerForm({
  patient,
  refAnswer,
  onChange,
}: {
  patient: Patient;
  refAnswer: RefAnswer;
  onChange: (newValue: RefAnswer) => void;
}) {
  let [value, setValue] = useState(refAnswer);

  function onNewValue(newValue: RefAnswer) {
    setValue({ ...newValue });
    onChange(newValue);
  }

  return (
    <div className="grid c1 gap20">
      <ReferenceFieldsForm
        patient={patient}
        currentAnswers={value.fieldAnswers}
        reference={value.reference}
        onChange={(newFieldAnswers) => {
          value.fieldAnswers = newFieldAnswers;
          onNewValue(value);
        }}
      ></ReferenceFieldsForm>
      <Input
        name={"Détails (optionnel)"}
        value={value.notes}
        type={"textarea"}
        shouldFocus={value.reference?.sections?.length == 0}
        placeholder={"Texte libre..."}
        rows={8}
        onChange={(newValue) => {
          value.notes = newValue;
          onNewValue(value);
        }}
      />
    </div>
  );
}
